import { apiAxios } from '@app/api-client';
import type {
  BulkUpdateStructureLinksInput,
  CartographyResponseDto,
  CreateGroupStructureLinkInput,
  CreateProcessStructureLinkInput,
  GroupProcessCreateInput,
  GroupProcessDto,
  GroupProcessUpdateInput,
  ItemCategoryCreateInput,
  ItemCategoryDto,
  ItemCategoryReorderInput,
  ItemCategoryTreeNodeDto,
  ItemCategoryUpdateInput,
  ProcessCreateInput,
  ProcessDto,
  ProcessListQuery,
  ProcessUpdateInput,
  ReplaceSiteEdgesInput,
  UpdateStructureLinkGeometryInput,
} from '@app/contracts';

export async function fetchCartography(siteId: string): Promise<CartographyResponseDto> {
  return apiAxios({
    url: `/api/v1/sites/${siteId}/cartography`,
    method: 'GET',
  });
}

export async function createGroupStructureLink(
  siteId: string,
  body: CreateGroupStructureLinkInput,
) {
  return apiAxios({
    url: `/api/v1/sites/${siteId}/cartography/group-links`,
    method: 'POST',
    data: body,
  });
}

export async function createProcessStructureLink(
  siteId: string,
  body: CreateProcessStructureLinkInput,
) {
  return apiAxios({
    url: `/api/v1/sites/${siteId}/cartography/process-links`,
    method: 'POST',
    data: body,
  });
}

export async function patchStructureLinkGeometry(
  siteId: string,
  linkId: string,
  body: UpdateStructureLinkGeometryInput,
) {
  return apiAxios({
    url: `/api/v1/sites/${siteId}/cartography/links/${linkId}`,
    method: 'PATCH',
    data: body,
  });
}

export async function bulkPatchStructureLinks(siteId: string, body: BulkUpdateStructureLinksInput) {
  return apiAxios({
    url: `/api/v1/sites/${siteId}/cartography/links`,
    method: 'PATCH',
    data: body,
  });
}

export async function deleteStructureLink(siteId: string, linkId: string) {
  return apiAxios({
    url: `/api/v1/sites/${siteId}/cartography/links/${linkId}`,
    method: 'DELETE',
  });
}

export async function putSiteEdges(siteId: string, body: ReplaceSiteEdgesInput) {
  return apiAxios({
    url: `/api/v1/sites/${siteId}/cartography/edges`,
    method: 'PUT',
    data: body,
  });
}

export async function fetchGroupProcesses(): Promise<GroupProcessDto[]> {
  return apiAxios({ url: '/api/v1/group-processes', method: 'GET' });
}

export async function createGroupProcess(body: GroupProcessCreateInput): Promise<GroupProcessDto> {
  return apiAxios({ url: '/api/v1/group-processes', method: 'POST', data: body });
}

export async function updateGroupProcess(
  id: string,
  body: GroupProcessUpdateInput,
): Promise<GroupProcessDto> {
  return apiAxios({ url: `/api/v1/group-processes/${id}`, method: 'PATCH', data: body });
}

export async function deleteGroupProcess(id: string) {
  return apiAxios({ url: `/api/v1/group-processes/${id}`, method: 'DELETE' });
}

export async function fetchProcesses(q?: ProcessListQuery): Promise<ProcessDto[]> {
  const params = new URLSearchParams();
  if (q?.groupProcessId) params.set('groupProcessId', q.groupProcessId);
  const qs = params.toString();
  return apiAxios({
    url: qs ? `/api/v1/processes?${qs}` : '/api/v1/processes',
    method: 'GET',
  });
}

export async function createProcess(body: ProcessCreateInput): Promise<ProcessDto> {
  return apiAxios({ url: '/api/v1/processes', method: 'POST', data: body });
}

export async function updateProcess(id: string, body: ProcessUpdateInput): Promise<ProcessDto> {
  return apiAxios({ url: `/api/v1/processes/${id}`, method: 'PATCH', data: body });
}

export async function deleteProcess(id: string) {
  return apiAxios({ url: `/api/v1/processes/${id}`, method: 'DELETE' });
}

export async function fetchItemCategoryTree(): Promise<ItemCategoryTreeNodeDto[]> {
  return apiAxios({ url: '/api/v1/item-categories/tree', method: 'GET' });
}

export async function createItemCategory(body: ItemCategoryCreateInput): Promise<ItemCategoryDto> {
  return apiAxios({ url: '/api/v1/item-categories', method: 'POST', data: body });
}

export async function updateItemCategory(
  id: string,
  body: ItemCategoryUpdateInput,
): Promise<ItemCategoryDto> {
  return apiAxios({ url: `/api/v1/item-categories/${id}`, method: 'PATCH', data: body });
}

export async function deleteItemCategory(id: string) {
  return apiAxios({ url: `/api/v1/item-categories/${id}`, method: 'DELETE' });
}

export async function reorderItemCategories(body: ItemCategoryReorderInput): Promise<void> {
  return apiAxios({ url: '/api/v1/item-categories/reorder', method: 'POST', data: body });
}
